import { supabase } from "@/integrations/supabase/client";

export interface Organization {
  id: string;
  subdomain: string;
  name: string;
  display_name?: string;
  logo_url?: string;
  theme_config: any;
  is_active: boolean;
}

export interface OrganizationMembership {
  id: string;
  user_id: string;
  org_id: string;
  role: 'admin' | 'member' | 'guest';
}

export class OrganizationService {
  /**
   * Resolve organization from various sources with fallback strategies
   */
  static resolveOrganizationSubdomain(): string {
    if (typeof window === 'undefined') return 'swift';
    
    const hostname = window.location.hostname;
    console.log('Resolving organization from hostname:', hostname);
    
    // Strategy 1: URL parameter override (works everywhere)
    const urlParams = new URLSearchParams(window.location.search);
    const orgParam = urlParams.get('org');
    if (orgParam) {
      console.log('Using org parameter:', orgParam);
      return orgParam;
    }
    
    // Strategy 2: localStorage preference (development helper)
    const devOrg = localStorage.getItem('dev-organization');
    if (devOrg) {
      console.log('Using localStorage org:', devOrg);
      return devOrg;
    }
    
    // Strategy 3: Production subdomain extraction
    if (this.isProductionEnvironment(hostname)) {
      const parts = hostname.split('.');
      if (parts.length >= 3) {
        const subdomain = parts[0];
        console.log('Extracted subdomain:', subdomain);
        return subdomain;
      }
    }
    
    // Fallback: Default organization
    console.log('Using default organization: swift');
    return 'swift';
  }

  /**
   * Check if we're in a production environment that supports subdomains
   */
  private static isProductionEnvironment(hostname: string): boolean {
    return !hostname.includes('localhost') && 
           !hostname.includes('127.0.0.1') && 
           !hostname.includes('.lovableproject.com') &&
           !hostname.includes('.sandbox.lovable.dev') &&
           !hostname.includes('vercel.app');
  }

  /**
   * Fetch organization by subdomain with fallback
   */
  static async fetchOrganization(subdomain: string): Promise<Organization> {
    console.log('Fetching organization for subdomain:', subdomain);
    
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('subdomain', subdomain)
      .eq('is_active', true)
      .single();

    if (error) {
      console.error('Organization fetch error:', error);
      
      // Fallback to Swift organization if original not found
      if (error.code === 'PGRST116' && subdomain !== 'swift') {
        console.log('Organization not found, falling back to Swift');
        
        const { data: swiftOrg, error: swiftError } = await supabase
          .from('organizations')
          .select('*')
          .eq('subdomain', 'swift')
          .eq('is_active', true)
          .single();
        
        if (swiftError) {
          console.error('Swift fallback error:', swiftError);
          throw new Error('No valid organization found');
        }
        
        return swiftOrg;
      }
      throw error;
    }

    console.log('Found organization:', data);
    return data;
  }

  /**
   * Check user's membership in organization
   */
  static async fetchMembership(authUserId: string, orgId: string): Promise<OrganizationMembership | null> {
    if (!authUserId || !orgId) return null;
    
    console.log('Checking membership for auth_user_id:', authUserId, 'in org:', orgId);
    
    // First get the user's profile ID from their auth_user_id
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id')
      .eq('auth_user_id', authUserId)
      .maybeSingle();

    if (profileError) {
      console.error('Profile lookup error:', profileError);
      throw profileError;
    }

    if (!profile) {
      console.log('No profile found for auth_user_id:', authUserId);
      return null;
    }

    console.log('Found profile ID:', profile.id, 'for auth_user_id:', authUserId);
    
    // Now check organization membership using the profile ID
    const { data, error } = await supabase
      .from('organization_user_roles')
      .select('*')
      .eq('user_id', profile.id)
      .eq('org_id', orgId)
      .maybeSingle();

    if (error) {
      console.error('Membership check error:', error);
      throw error;
    }

    console.log('Found membership:', data);
    return data;
  }

  /**
   * Check if user has access to organization
   */
  static hasOrganizationAccess(membership: OrganizationMembership | null): boolean {
    return !!membership; // Must have membership
  }

  /**
   * Get user's role in organization
   */
  static getOrganizationRole(membership: OrganizationMembership | null): 'admin' | 'member' | 'guest' {
    return membership?.role || 'guest';
  }

  /**
   * Check if user is admin in organization
   */
  static isOrganizationAdmin(membership: OrganizationMembership | null): boolean {
    return membership?.role === 'admin';
  }
}